import { useState, useRef, useEffect, useCallback } from "react";
import {
  STAGES, ALL_TAGS, SOURCES, CONTACT_METHODS,
  PIPELINES, PRIORITY_LEVELS, PRIORITY_COLOR,
  TITLES, INDUSTRIES, CONTACT_CATEGORIES,
} from "./constants";
import {
  S, Ic, Avatar, StagePill, TagChip, ProbBar,
  Dropdown, Field, Input, Sel, Textarea, fmt,
} from "./ui";
import { supabase } from "../../../../lib/supabaseClient";
import { insertLog } from "../../../../services/logService";

// ─────────────────────────────────────────────────────────────
const ACT_TYPE = {
  comment:      { bg: "#E3EAFF", color: "#3761E9", icon: "💬", label: "Comment"          },
  request:      { bg: "#FFF3E0", color: "#FB8C00", icon: "🔔", label: "Request Response" },
  email:        { bg: "#FFF8E1", color: "#F9A825", icon: "✉️",  label: "Email"            },
  task:         { bg: "#E0F2F1", color: "#00897B", icon: "✅", label: "Task"             },
  event:        { bg: "#F3E5F5", color: "#8E24AA", icon: "📅", label: "Event"            },
  note:         { bg: "#FFF8E1", color: "#F57F17", icon: "📝", label: "Note"             },
  call:         { bg: "#E8F5E9", color: "#2E7D32", icon: "📞", label: "Call"             },
  stage_change: { bg: "#F5F5F5", color: "#757575", icon: "🔄", label: "Stage Change"     },
};

const INPUT_MODES = ["comment", "request", "email", "task", "event"];
const MAIN_TABS   = ["Main", "Documents", "Activities", "Email", "Calls", "Integrations", "Automation"];
const PHONE_TYPES = ["Work", "Personal", "Other"];
const EMAIL_TYPES = ["Work", "Personal", "Other"];

// ─────────────────────────────────────────────────────────────
// CREATE CONTACT DRAWER
// ─────────────────────────────────────────────────────────────
function CreateContactDrawer({ onClose, onSave }) {
  const [form, setForm] = useState({
    title: "Mr.", firstName: "", lastName: "", category: "Lead",
    industry: "", website: "",
    phones: [{ type: "Work", value: "" }],
    emails: [{ type: "Work", value: "" }],
    description: "",
  });
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const addPhone = () => setForm(p => ({ ...p, phones: [...p.phones, { type: "Work", value: "" }] }));
  const addEmail = () => setForm(p => ({ ...p, emails: [...p.emails, { type: "Work", value: "" }] }));
  const setPhone = (i, key, val) => setForm(p => ({ ...p, phones: p.phones.map((ph, idx) => idx === i ? { ...ph, [key]: val } : ph) }));
  const setEmail = (i, key, val) => setForm(p => ({ ...p, emails: p.emails.map((em, idx) => idx === i ? { ...em, [key]: val } : em) }));

  const save = () => {
    const name = [form.firstName, form.lastName].filter(Boolean).join(" ");
    if (!name) return;
    onSave({ id: Date.now(), name, phone: form.phones[0]?.value || "", email: form.emails[0]?.value || "", category: form.category, industry: form.industry });
    onClose();
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 600, display: "flex", justifyContent: "flex-end" }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width: "460px", background: "#fff", height: "100%", display: "flex", flexDirection: "column", boxShadow: "-6px 0 30px rgba(0,0,0,0.18)", animation: "slideInRight .2s ease" }}>
        <div style={{ padding: "14px 18px", background: "linear-gradient(135deg,#1a1a2e,#0f3460)", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div style={{ fontSize: "14px", fontWeight: "700", color: "#fff" }}>New Account / Contact</div>
          <button onClick={onClose} style={{ border: "none", background: "rgba(255,255,255,0.15)", borderRadius: "7px", width: "26px", height: "26px", cursor: "pointer", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px" }}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 18px" }}>
          <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
            <div style={{ width: "100px" }}><Field label="Title"><Sel value={form.title} onChange={e => f("title", e.target.value)}>{TITLES.map(t => <option key={t}>{t}</option>)}</Sel></Field></div>
            <div style={{ flex: 1 }}><Field label="First Name"><Input value={form.firstName} onChange={e => f("firstName", e.target.value)} placeholder="First Name" /></Field></div>
            <div style={{ flex: 1 }}><Field label="Last Name"><Input value={form.lastName} onChange={e => f("lastName", e.target.value)} placeholder="Last Name" /></Field></div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
            <Field label="Category"><Sel value={form.category} onChange={e => f("category", e.target.value)}>{CONTACT_CATEGORIES.map(c => <option key={c}>{c}</option>)}</Sel></Field>
            <Field label="Industry"><Sel value={form.industry} onChange={e => f("industry", e.target.value)}><option value="">— Select —</option>{INDUSTRIES.map(ind => <option key={ind}>{ind}</option>)}</Sel></Field>
          </div>
          <Field label="Phone">
            {form.phones.map((ph, i) => (
              <div key={i} style={{ display: "flex", gap: "6px", marginBottom: "6px" }}>
                <Sel value={ph.type} onChange={e => setPhone(i, "type", e.target.value)} style={{ width: "100px" }}>{PHONE_TYPES.map(t => <option key={t}>{t}</option>)}</Sel>
                <Input value={ph.value} onChange={e => setPhone(i, "value", e.target.value)} placeholder="+966 5X XXX XXXX" type="tel" />
              </div>
            ))}
            <button onClick={addPhone} style={{ fontSize: "11.5px", color: "#3761E9", background: "none", border: "none", cursor: "pointer" }}>+ Add phone</button>
          </Field>
          <Field label="Email">
            {form.emails.map((em, i) => (
              <div key={i} style={{ display: "flex", gap: "6px", marginBottom: "6px" }}>
                <Sel value={em.type} onChange={e => setEmail(i, "type", e.target.value)} style={{ width: "100px" }}>{EMAIL_TYPES.map(t => <option key={t}>{t}</option>)}</Sel>
                <Input value={em.value} onChange={e => setEmail(i, "value", e.target.value)} placeholder="email@company.com" type="email" />
              </div>
            ))}
            <button onClick={addEmail} style={{ fontSize: "11.5px", color: "#3761E9", background: "none", border: "none", cursor: "pointer" }}>+ Add email</button>
          </Field>
          <Field label="Description"><Textarea value={form.description} onChange={e => f("description", e.target.value)} placeholder="Short description..." rows={3} /></Field>
        </div>
        <div style={{ padding: "12px 18px", borderTop: "1px solid #ecedf2", display: "flex", justifyContent: "flex-end", gap: "8px", background: "#fafafa" }}>
          <button onClick={onClose} style={{ padding: "8px 16px", border: "1.5px solid #e0e0e8", borderRadius: "8px", background: "#fff", cursor: "pointer", fontSize: "12.5px", color: "#6e6e80" }}>Cancel</button>
          <button onClick={save} style={{ padding: "8px 18px", border: "none", borderRadius: "8px", background: "#0f3460", color: "#fff", cursor: "pointer", fontSize: "12.5px", fontWeight: "600" }}>Save</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// STAGE CHEVRONS
// ─────────────────────────────────────────────────────────────
function StageChevrons({ stages, currentStage, onSelect }) {
  const currentIdx = stages.indexOf(currentStage);
  return (
    <div style={{ display: "flex", width: "100%", height: "34px" }}>
      {stages.map((s, i) => {
        const isActive = s === currentStage, isCompleted = i < currentIdx;
        const isFirst = i === 0, isLast = i === stages.length - 1;
        const bg = isActive ? "#3761E9" : isCompleted ? "#dce8ff" : "#fff";
        const clr = isActive ? "#fff" : isCompleted ? "#3761E9" : "#a3aed0";
        const brd = isActive ? "#3761E9" : isCompleted ? "#b8d0ff" : "#e6e9f2";
        const pathD = isFirst ? "M0,0 H88 L100,17 L88,34 H0 Z" : isLast ? "M0,0 L12,17 L0,34 H100 V0 Z" : "M0,0 L12,17 L0,34 H88 L100,17 L88,34 Z";
        return (
          <div key={s} onClick={() => onSelect(s)} title={`→ ${s}`}
            style={{ flex: 1, height: "34px", cursor: "pointer", marginLeft: isFirst ? 0 : "-9px", zIndex: stages.length - i, position: "relative", transition: "opacity .15s" }}
            onMouseEnter={e => { if (!isActive) e.currentTarget.style.opacity = "0.78"; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}>
            <svg width="100%" height="100%" viewBox="0 0 100 34" preserveAspectRatio="none" style={{ position: "absolute", inset: 0, overflow: "visible" }}>
              <path d={pathD} fill={bg} stroke={brd} strokeWidth="1" vectorEffect="non-scaling-stroke" />
            </svg>
            <div style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", justifyContent: "center", height: "100%", paddingLeft: isFirst ? "6px" : "14px", paddingRight: isLast ? "6px" : "4px", fontSize: "8.5px", fontWeight: "800", textTransform: "uppercase", letterSpacing: "0.5px", color: clr, whiteSpace: "nowrap", overflow: "hidden", userSelect: "none" }}>
              {isCompleted && "✓ "}{s}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ATTACHMENT ITEM
// ─────────────────────────────────────────────────────────────
function AttachmentItem({ file, onRemove }) {
  const icon = file.type?.includes("pdf") ? "📄" : file.type?.includes("image") ? "🖼️" : file.type?.includes("word") ? "📝" : file.type?.includes("sheet") || file.type?.includes("excel") ? "📊" : "📎";
  const url = file.publicUrl || file.url;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "9px 12px", background: "#f8f8fc", borderRadius: "9px", border: "1px solid #e0e0e8" }}>
      <span style={{ fontSize: "18px", flexShrink: 0 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <a href={url || "#"} target="_blank" rel="noopener noreferrer"
          style={{ fontSize: "12.5px", fontWeight: "600", color: "#3761E9", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block", textDecoration: "underline" }}>
          {file.name}
        </a>
        <div style={{ fontSize: "10.5px", color: "#a0a0b8" }}>
          {file.size ? (file.size / 1024).toFixed(1) + " KB" : ""}
          {file.uploading && " — uploading..."}
          {file.uploadError && <span style={{ color: "#E53935" }}> — upload failed</span>}
        </div>
      </div>
      {onRemove && <button onClick={onRemove} style={{ border: "none", background: "none", cursor: "pointer", color: "#E53935", fontSize: "15px" }}>×</button>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ACTIVITY INPUT BOX
// ─────────────────────────────────────────────────────────────
function ActivityInputBox({ onAdd, currentUser, oppId }) {
  const [mode, setMode]           = useState("comment");
  const [text, setText]           = useState("");
  const [emailTo, setEmailTo]     = useState("");
  const [loading, setLoading]     = useState(false);
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef();

  const uploadFile = async (fileObj, blobUrl) => {
    const safeName = fileObj.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const path = `crm/${oppId || "general"}/${Date.now()}_${safeName}`;
    try {
      const { error } = await supabase.storage.from("crm-attachments").upload(path, fileObj, { upsert: true });
      if (error) return blobUrl;
      const { data: urlData } = supabase.storage.from("crm-attachments").getPublicUrl(path);
      return urlData?.publicUrl || blobUrl;
    } catch { return blobUrl; }
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const newItems = files.map(f => ({
      name: f.name, size: f.size, type: f.type,
      url: URL.createObjectURL(f), publicUrl: URL.createObjectURL(f),
      uploading: true, uploadError: false, _file: f,
    }));
    setAttachments(prev => [...prev, ...newItems]);
    for (const item of newItems) {
      const finalUrl = await uploadFile(item._file, item.url);
      setAttachments(prev => prev.map(a => a._file === item._file ? { ...a, publicUrl: finalUrl, url: finalUrl, uploading: false } : a));
    }
    e.target.value = "";
  };

  const post = async () => {
    if (!text.trim() && attachments.length === 0) return;
    setLoading(true);
    const activity = {
      id: Date.now(), type: mode, text: text.trim(),
      date: new Date().toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }),
      user: currentUser?.name || "—",
      attachments: attachments.map(a => ({ name: a.name, size: a.size, type: a.type, publicUrl: a.publicUrl || a.url, url: a.publicUrl || a.url })),
    };
    await onAdd(activity); // ← awaits Supabase save
    setText(""); setAttachments([]); setLoading(false);
  };

  return (
    <div style={{ background: "#fff", borderRadius: "12px", border: "1px solid #e6e9f2", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", overflow: "hidden" }}>
      <div style={{ display: "flex", background: "#fcfcfd", borderBottom: "1px solid #e6e9f2", overflowX: "auto" }}>
        {INPUT_MODES.map(m => (
          <button key={m} onClick={() => setMode(m)} style={{ padding: "8px 12px", border: "none", background: "transparent", cursor: "pointer", fontSize: "10px", fontWeight: "800", textTransform: "uppercase", letterSpacing: "0.7px", color: mode === m ? "#3761E9" : "#a3aed0", borderBottom: mode === m ? "2px solid #3761E9" : "2px solid transparent", transition: "all .15s", whiteSpace: "nowrap" }}>
            {m === "request" ? "Request Response" : m}
          </button>
        ))}
      </div>
      {mode === "email" && (
        <div style={{ padding: "7px 14px", borderBottom: "1px solid #e6e9f2", background: "#f8faff" }}>
          <input value={emailTo} onChange={e => setEmailTo(e.target.value)} placeholder="Recipient email..."
            style={{ width: "100%", padding: "5px 10px", border: "1px solid #e6e9f2", borderRadius: "7px", fontSize: "12.5px", outline: "none", background: "#fff", color: "#2b3674" }} />
        </div>
      )}
      <textarea value={text} onChange={e => setText(e.target.value)}
        placeholder={mode === "comment" ? "Write a comment... (Ctrl+Enter to send)" : mode === "request" ? "Request a response from the team..." : mode === "email" ? "Write your email..." : mode === "task" ? "Describe the task..." : "Describe the event..."}
        rows={3} onKeyDown={e => { if (e.ctrlKey && e.key === "Enter") post(); }}
        style={{ width: "100%", padding: "11px 14px", border: "none", outline: "none", fontSize: "13px", color: "#2b3674", lineHeight: "1.6", resize: "vertical", fontFamily: "inherit", background: "#fff", minHeight: "82px" }} />
      {attachments.length > 0 && (
        <div style={{ padding: "8px 14px", borderTop: "1px solid #f0f0f8", display: "flex", flexDirection: "column", gap: "6px", background: "#fafbff" }}>
          {attachments.map((a, i) => <AttachmentItem key={i} file={a} onRemove={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))} />)}
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 12px", background: "#fcfcfd", borderTop: "1px solid #e6e9f2" }}>
        <div style={{ display: "flex", gap: "2px" }}>
          <input ref={fileInputRef} type="file" multiple style={{ display: "none" }} onChange={handleFileChange} />
          <button title="Attach file" onClick={() => fileInputRef.current?.click()} style={{ width: "26px", height: "26px", border: "none", background: "none", cursor: "pointer", borderRadius: "5px", fontSize: "14px" }}>📎</button>
          <button title="Bold" onClick={() => setText(t => t + "**bold**")} style={{ width: "26px", height: "26px", border: "none", background: "none", cursor: "pointer", borderRadius: "5px", fontSize: "12px", fontWeight: "900", color: "#9e9eb8" }}>B</button>
          <button title="Italic" onClick={() => setText(t => t + "_italic_")} style={{ width: "26px", height: "26px", border: "none", background: "none", cursor: "pointer", borderRadius: "5px", fontSize: "12px", fontStyle: "italic", color: "#9e9eb8" }}>I</button>
          <button title="Bullet" onClick={() => setText(t => t ? t + "\n• " : "• ")} style={{ width: "26px", height: "26px", border: "none", background: "none", cursor: "pointer", borderRadius: "5px", fontSize: "13px", color: "#9e9eb8" }}>≡</button>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {attachments.some(a => a.uploading) && <span style={{ fontSize: "10px", color: "#FB8C00", fontWeight: "600" }}>Uploading...</span>}
          {attachments.length > 0 && !attachments.some(a => a.uploading) && <span style={{ fontSize: "10px", color: "#43A047", fontWeight: "600" }}>{attachments.length} file(s) ready</span>}
          <button onClick={post} disabled={(!text.trim() && attachments.length === 0) || loading}
            style={{ padding: "6px 18px", border: "none", borderRadius: "7px", background: (text.trim() || attachments.length > 0) ? "#3761E9" : "#e8e8f0", color: (text.trim() || attachments.length > 0) ? "#fff" : "#a0a0b8", fontSize: "10.5px", fontWeight: "800", textTransform: "uppercase", letterSpacing: "0.8px", cursor: (text.trim() || attachments.length > 0) ? "pointer" : "not-allowed", transition: "all .15s" }}>
            {loading ? "Saving..." : "Post"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ACTIVITY FEED
// ─────────────────────────────────────────────────────────────
function ActivityFeed({ activities, loading }) {
  if (loading) return <div style={{ textAlign: "center", padding: "30px", color: "#a3aed0", fontSize: "12px" }}>Loading activities...</div>;
  if (!activities?.length) return (
    <div style={{ textAlign: "center", padding: "40px 20px", color: "#c0c0d8" }}>
      <div style={{ fontSize: "30px", marginBottom: "8px" }}>💬</div>
      <div style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "1px" }}>No activities yet</div>
    </div>
  );
  return (
    <div>
      <div style={{ fontSize: "10px", fontWeight: "800", color: "#a3aed0", textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: "12px" }}>History ({activities.length})</div>
      {activities.map((act, i) => {
        const cfg = ACT_TYPE[act.type] || ACT_TYPE.note;
        const isStage = act.type === "stage_change";
        const isLast = i === activities.length - 1;
        const parts = (act.text || "").split("->");
        return (
          <div key={act.id || i} style={{ display: "flex", gap: "11px", position: "relative", paddingBottom: isLast ? 0 : "15px" }}>
            {!isLast && <div style={{ position: "absolute", left: "12px", top: "26px", bottom: 0, width: "1px", background: "#e6e9f2" }} />}
            <div style={{ width: "26px", height: "26px", borderRadius: "50%", background: cfg.bg, flexShrink: 0, zIndex: 1, display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid #fff", boxShadow: `0 0 0 1.5px ${cfg.color}30`, fontSize: "12px" }}>
              {cfg.icon}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  <span style={{ fontSize: "11.5px", fontWeight: "800", color: "#2b3674" }}>{act.user || "—"}</span>
                  <span style={{ fontSize: "10.5px", color: "#a3aed0" }}>{isStage ? "changed stage" : cfg.label}</span>
                </div>
                <span style={{ fontSize: "10px", color: "#b0b0c8", flexShrink: 0 }}>{act.date}</span>
              </div>
              {isStage ? (
                <div style={{ display: "inline-flex", alignItems: "center", gap: "7px", background: "#f4f7fe", padding: "5px 11px", borderRadius: "7px", border: "1px solid #e6e9f2", fontSize: "11.5px", fontWeight: "700", color: "#2b3674" }}>
                  <span>{parts[0]?.trim()}</span><span style={{ color: "#a3aed0" }}>→</span><span style={{ color: "#3761E9" }}>{parts[1]?.trim()}</span>
                </div>
              ) : (
                <div style={{ background: "#fff", border: "1px solid #e6e9f2", borderRadius: "9px", padding: "9px 12px", fontSize: "12.5px", color: "#2b3674", lineHeight: "1.6", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                  {act.text && <p style={{ marginBottom: act.attachments?.length ? "8px" : 0, whiteSpace: "pre-wrap" }}>{act.text}</p>}
                  {act.attachments?.length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "5px", marginTop: "6px" }}>
                      {act.attachments.map((file, fi) => {
                        const isImage = file.type?.includes("image");
                        const url = file.publicUrl || file.url;
                        return (
                          <div key={fi}>
                            {isImage && url && (
                              <a href={url} target="_blank" rel="noopener noreferrer" style={{ display: "block", marginBottom: "4px" }}>
                                <img src={url} alt={file.name} style={{ maxWidth: "200px", maxHeight: "140px", objectFit: "cover", borderRadius: "7px", border: "1px solid #e6e9f2", cursor: "pointer" }} onError={e => e.target.style.display = "none"} />
                              </a>
                            )}
                            <a href={url} target="_blank" rel="noopener noreferrer"
                              style={{ display: "inline-flex", alignItems: "center", gap: "5px", padding: "4px 9px", background: "#f4f7fe", border: "1px solid #e6e9f2", borderRadius: "6px", fontSize: "11px", fontWeight: "600", color: "#3761E9", textDecoration: "none" }}>
                              📎 {file.name}
                            </a>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SHARED UI HELPERS
// ─────────────────────────────────────────────────────────────
function SideCard({ title, children }) {
  return (
    <div style={{ background: "#fff", borderRadius: "13px", border: "1px solid #e6e9f2", padding: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
      <div style={{ fontSize: "9px", fontWeight: "800", color: "#a3aed0", textTransform: "uppercase", letterSpacing: "2px", marginBottom: "13px" }}>{title}</div>
      {children}
    </div>
  );
}
function DetailRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px", paddingBottom: "7px", marginBottom: "7px", borderBottom: "1px solid #f5f5fb" }}>
      <span style={{ fontSize: "11.5px", color: "#8888a8", fontWeight: "600", flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: "12px", color: "#2b3674", fontWeight: "600", textAlign: "right", wordBreak: "break-all" }}>{value || <span style={{ color: "#c8c8e0" }}>—</span>}</span>
    </div>
  );
}
function IBtn({ title, icon, onClick }) {
  return (
    <button title={title} onClick={onClick}
      style={{ width: "30px", height: "30px", border: "1px solid #e6e9f2", borderRadius: "7px", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#a3aed0", transition: "all .15s" }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = "#3761E9"; e.currentTarget.style.color = "#3761E9"; e.currentTarget.style.background = "#f0f4ff"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = "#e6e9f2"; e.currentTarget.style.color = "#a3aed0"; e.currentTarget.style.background = "#fff"; }}>
      <Ic n={icon} s={13} />
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// INTEGRATIONS TAB
// ─────────────────────────────────────────────────────────────
function IntegrationsTab() {
  const integrations = [
    { name: "Slack",       icon: "💬", desc: "Post deal updates to Slack channels",      status: "Connect" },
    { name: "Gmail",       icon: "✉️",  desc: "Sync emails from Gmail automatically",     status: "Connect" },
    { name: "Calendar",    icon: "📅", desc: "Sync events with Google Calendar",          status: "Connect" },
    { name: "WhatsApp",    icon: "📱", desc: "Send WhatsApp messages from CRM",           status: "Connect" },
    { name: "Zapier",      icon: "⚡", desc: "Connect to 5,000+ apps via Zapier",         status: "Connect" },
    { name: "HubSpot",     icon: "🔶", desc: "Sync contacts and deals with HubSpot",      status: "Connect" },
  ];
  return (
    <div style={{ background: "#fff", borderRadius: "13px", border: "1px solid #e6e9f2", padding: "20px" }}>
      <div style={{ fontSize: "10px", fontWeight: "800", color: "#a3aed0", textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: "16px" }}>Available Integrations</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
        {integrations.map(intg => (
          <div key={intg.name} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "14px", border: "1.5px solid #e6e9f2", borderRadius: "10px", background: "#fafbff" }}>
            <span style={{ fontSize: "24px", flexShrink: 0 }}>{intg.icon}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: "13px", fontWeight: "700", color: "#2b3674", marginBottom: "3px" }}>{intg.name}</div>
              <div style={{ fontSize: "10.5px", color: "#a3aed0", marginBottom: "8px" }}>{intg.desc}</div>
              <button style={{ padding: "4px 12px", border: "1.5px solid #3761E9", borderRadius: "6px", background: "#fff", color: "#3761E9", fontSize: "11px", fontWeight: "700", cursor: "pointer" }}
                onMouseEnter={e => { e.currentTarget.style.background = "#3761E9"; e.currentTarget.style.color = "#fff"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#3761E9"; }}>
                {intg.status}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// AUTOMATION TAB
// ─────────────────────────────────────────────────────────────
function AutomationTab({ opp }) {
  const [rules, setRules] = useState([
    { id: 1, trigger: "Stage → Qualification",  action: "Send email to assignee",    active: true  },
    { id: 2, trigger: "Stage → Proposal",       action: "Create follow-up task",      active: true  },
    { id: 3, trigger: "Probability > 70%",      action: "Notify sales manager",       active: false },
    { id: 4, trigger: "Close date in 3 days",   action: "Send reminder notification", active: true  },
    { id: 5, trigger: "Stage → Closed Won",     action: "Log win to Security Logs",   active: true  },
  ]);
  const toggle = id => setRules(prev => prev.map(r => r.id === id ? { ...r, active: !r.active } : r));
  return (
    <div style={{ background: "#fff", borderRadius: "13px", border: "1px solid #e6e9f2", padding: "20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <div style={{ fontSize: "10px", fontWeight: "800", color: "#a3aed0", textTransform: "uppercase", letterSpacing: "1.5px" }}>Automation Rules</div>
        <button style={{ padding: "5px 12px", border: "none", borderRadius: "7px", background: "#3761E9", color: "#fff", fontSize: "11px", fontWeight: "700", cursor: "pointer" }}>+ New Rule</button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {rules.map(rule => (
          <div key={rule.id} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "12px 14px", border: `1.5px solid ${rule.active ? "#dce8ff" : "#e6e9f2"}`, borderRadius: "10px", background: rule.active ? "#f8faff" : "#fafafa" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "3px" }}>
                <span style={{ fontSize: "10.5px", background: "#E3EAFF", color: "#3761E9", borderRadius: "5px", padding: "2px 7px", fontWeight: "700" }}>WHEN</span>
                <span style={{ fontSize: "12.5px", fontWeight: "600", color: "#2b3674" }}>{rule.trigger}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <span style={{ fontSize: "10.5px", background: "#E0F2F1", color: "#00897B", borderRadius: "5px", padding: "2px 7px", fontWeight: "700" }}>THEN</span>
                <span style={{ fontSize: "12px", color: "#6e6e80" }}>{rule.action}</span>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
              <span style={{ fontSize: "10.5px", color: rule.active ? "#00897B" : "#a3aed0", fontWeight: "700" }}>{rule.active ? "Active" : "Off"}</span>
              <div onClick={() => toggle(rule.id)}
                style={{ width: "36px", height: "20px", borderRadius: "10px", background: rule.active ? "#3761E9" : "#e0e0e8", cursor: "pointer", position: "relative", transition: "background .2s" }}>
                <div style={{ position: "absolute", top: "2px", left: rule.active ? "18px" : "2px", width: "16px", height: "16px", borderRadius: "50%", background: "#fff", transition: "left .2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// MAIN DRAWER INNER
// ═════════════════════════════════════════════════════════════
function OpportunityDetailsDrawerInner({ opp, onClose, onUpdate, onDelete, onStageChange, teamMembers = [] }) {
  const [tab,         setTab]         = useState("Main");
  const [editing,     setEditing]     = useState(false);
  const [ef,          setEf]          = useState({ ...opp });
  const [confirmDel,  setConfirmDel]  = useState(false);
  const [activities,  setActivities]  = useState([]);
  const [actsLoading, setActsLoading] = useState(true);
  const [copied,      setCopied]      = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [contacts,    setContacts]    = useState(opp?.contact ? [{ id: 1, name: opp.contact, phone: opp.phone || "", email: opp.email || "" }] : []);
  const [showInvite,  setShowInvite]  = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [assignees,   setAssignees]   = useState(opp?.assignees?.length > 0 ? opp.assignees : (opp?.assignee ? [opp.assignee] : []));
  const fileRef = useRef();

  // ── Load activities from Supabase ──
  const loadActivities = useCallback(async () => {
    if (!opp?.id || String(opp.id).startsWith("local_")) {
      setActivities([]); setActsLoading(false); return;
    }
    setActsLoading(true);
    const { data, error } = await supabase
      .from("activities")
      .select("*")
      .eq("opportunity_id", opp.id)
      .order("created_at", { ascending: false });

    if (error) { console.warn("[Activities] load:", error.message); setActivities([]); }
    else {
      setActivities((data || []).map(a => ({
        id:          a.id,
        type:        a.type || "comment",
        text:        a.content || "",
        user:        a.actor_name || "—",
        date:        a.created_at ? new Date(a.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—",
        attachments: Array.isArray(a.attachments) ? a.attachments : [],
      })));
    }
    setActsLoading(false);
  }, [opp?.id]);

  useEffect(() => { loadActivities(); }, [loadActivities]);

  // ── Derived activity stats ──
  const todayStr     = new Date().toDateString();
  const todayCount   = activities.filter(a => { try { return new Date(a.date).toDateString() === todayStr; } catch { return false; } }).length;
  const noActCount   = activities.length === 0 ? 1 : 0;
  const overdueCount = activities.filter(a => a.type === "task" && a.dueDate && new Date(a.dueDate) < new Date()).length;

  const set = (k, v) => setEf(p => ({ ...p, [k]: v }));
  const save   = () => { onUpdate({ ...ef, assignee: assignees[0] || null, assignees: assignees }); setEditing(false); };
  const cancel = () => { setEf({ ...opp }); setEditing(false); };
  const startEdit = () => { setEditing(true); setEf({ ...opp }); setTab("Main"); };
  const teamList = teamMembers.length > 0 ? teamMembers : [];

  const handleStage = s => {
    const prev = opp.stage;
    onStageChange(opp.id, s);
    onUpdate({ ...opp, stage: s });
    const stageAct = {
      id: "opt_" + Date.now(), type: "stage_change",
      text: `${prev} -> ${s}`, user: assignees[0]?.name || "—",
      date: new Date().toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
      attachments: [],
    };
    setActivities(prev2 => [stageAct, ...prev2]);
  };

  // ── Add activity → Supabase + local state ──
  const addActivity = useCallback(async (act) => {
    // Show immediately (optimistic)
    setActivities(prev => [act, ...prev]);
    // Persist to Supabase
    if (opp?.id && !String(opp.id).startsWith("local_")) {
      const { error } = await supabase.from("activities").insert({
        opportunity_id: opp.id,
        type:           act.type || "comment",
        content:        act.text || "",
        actor_name:     act.user || assignees[0]?.name || "—",
        attachments:    act.attachments || [],
      });
      if (error) {
        console.warn("[Activities] save failed:", error.message);
      } else {
        await insertLog({
          action:     "CRM_ACTIVITY",
          actor_name: act.user || assignees[0]?.name || "System",
          details:    `${act.type} on opportunity "${opp.name}"`,
          timestamp:  new Date().toISOString(),
          module:     "CRM",
        }).catch(() => {});
        // Reload to get server timestamp
        await loadActivities();
      }
    }
  }, [opp?.id, opp?.name, assignees, loadActivities]);

  const toggleTag = tag => setEf(p => ({
    ...p,
    tags: p.tags?.some(t => t.label === tag.label) ? p.tags.filter(t => t.label !== tag.label) : [...(p.tags || []), tag],
  }));

  // Documents upload
  const handleDocFiles = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const newFiles = files.map(file => ({
      name: file.name, size: file.size, type: file.type,
      url: URL.createObjectURL(file), publicUrl: URL.createObjectURL(file),
      uploading: true, uploadError: false, _file: file,
    }));
    setEf(p => ({ ...p, attachments: [...(p.attachments || []), ...newFiles] }));
    for (const item of newFiles) {
      const safeName = item.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `crm/${opp.id || "general"}/${Date.now()}_${safeName}`;
      const { error } = await supabase.storage.from("crm-attachments").upload(path, item._file, { upsert: true });
      let publicUrl = item.url;
      if (!error) { const { data: u } = supabase.storage.from("crm-attachments").getPublicUrl(path); publicUrl = u?.publicUrl || item.url; }
      setEf(p => ({ ...p, attachments: (p.attachments || []).map(a => a._file === item._file ? { ...a, publicUrl, url: publicUrl, uploading: false } : a) }));
    }
  };
  const removeDocFile = idx => setEf(p => ({ ...p, attachments: (p.attachments || []).filter((_, i) => i !== idx) }));
  const copyPhone = () => { navigator.clipboard?.writeText(opp.phone || "").then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }); };
  const Vdiv = () => <div style={{ width: "1px", height: "20px", background: "#e6e9f2", flexShrink: 0 }} />;

  return (
    <>
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", backdropFilter: "blur(2px)", zIndex: 300, display: "flex", justifyContent: "flex-end" }}
        onClick={e => e.target === e.currentTarget && onClose()}>
        <div style={{ width: "min(1080px, 94vw)", background: "#f4f7fe", height: "100%", display: "flex", flexDirection: "column", boxShadow: "-8px 0 36px rgba(0,0,0,0.13)", animation: "slideInRight .22s ease" }}>

          {/* ══ HEADER ══ */}
          <div style={{ background: "#fff", borderBottom: "1px solid #e6e9f2", padding: "14px 22px 0", flexShrink: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "11px" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: "9.5px", color: "#a3aed0", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "4px" }}>
                  Opportunities › {opp.pipeline || "Sales Pipeline"}
                </div>
                {editing
                  ? <input value={ef.name} onChange={e => set("name", e.target.value)} autoFocus style={{ fontSize: "18px", fontWeight: "900", color: "#2b3674", border: "2px solid #3761E9", borderRadius: "8px", padding: "3px 10px", outline: "none", width: "100%", maxWidth: "480px", background: "#fff" }} />
                  : <h1 style={{ fontSize: "18px", fontWeight: "900", color: "#2b3674", letterSpacing: "-0.3px", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{opp.name}</h1>
                }
              </div>
              <div style={{ display: "flex", gap: "5px", alignItems: "center", marginLeft: "14px", flexShrink: 0 }}>
                {editing ? (
                  <>
                    <button onClick={cancel} style={{ padding: "6px 14px", border: "1.5px solid #e0e0e8", borderRadius: "7px", background: "#fff", cursor: "pointer", fontSize: "12px", fontWeight: "600", color: "#6e6e80" }}>Cancel</button>
                    <button onClick={save} style={{ padding: "6px 18px", border: "none", borderRadius: "7px", background: "#3761E9", color: "#fff", cursor: "pointer", fontSize: "12px", fontWeight: "700", boxShadow: "0 2px 8px rgba(55,97,233,0.3)" }}>Save</button>
                  </>
                ) : (
                  <>
                    <IBtn title="Send Email" icon="mail" onClick={() => opp.email && window.open(`mailto:${opp.email}`)} />
                    <IBtn title="Call" icon="phone" onClick={() => opp.phone && window.open(`tel:${opp.phone}`)} />
                    <Vdiv />
                    <button onClick={startEdit} style={{ display: "flex", alignItems: "center", gap: "5px", padding: "6px 16px", border: "none", borderRadius: "7px", background: "#3761E9", color: "#fff", cursor: "pointer", fontSize: "12px", fontWeight: "700" }}>
                      <Ic n="edit" s={11} c="#fff" /> Edit
                    </button>
                    <Dropdown right
                      trigger={<button style={{ width: "30px", height: "30px", border: "1px solid #e6e9f2", borderRadius: "7px", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#a3aed0" }}><Ic n="moreV" s={13} /></button>}
                      items={[
                        { label: "Archive",   icon: "archive", onClick: () => { onUpdate({ ...opp, archived: true }); onClose(); } },
                        { label: "Duplicate", icon: "copy",    onClick: () => {} },
                        "---",
                        { label: "Delete", icon: "trash", danger: true, onClick: () => setConfirmDel(true) },
                      ]}
                    />
                    <button onClick={onClose}
                      style={{ width: "30px", height: "30px", border: "none", borderRadius: "7px", background: "#f4f7fe", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#a3aed0", transition: "all .15s" }}
                      onMouseEnter={e => { e.currentTarget.style.background = "#fde8e8"; e.currentTarget.style.color = "#e53935"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "#f4f7fe"; e.currentTarget.style.color = "#a3aed0"; }}>
                      <Ic n="close" s={11} />
                    </button>
                  </>
                )}
              </div>
            </div>
            <div style={{ display: "flex", overflowX: "auto" }}>
              {MAIN_TABS.map(t => (
                <button key={t} onClick={() => setTab(t)} style={{ padding: "8px 14px", border: "none", background: "transparent", cursor: "pointer", fontSize: "12.5px", fontWeight: tab === t ? "700" : "500", color: tab === t ? "#3761E9" : "#a3aed0", borderBottom: tab === t ? "2.5px solid #3761E9" : "2.5px solid transparent", transition: "all .15s", whiteSpace: "nowrap" }}>{t}</button>
              ))}
            </div>
          </div>

          {/* ══ STAGE BAR ══ */}
          <div style={{ background: "#fff", padding: "7px 22px", borderBottom: "1px solid #e6e9f2", flexShrink: 0 }}>
            <StageChevrons stages={STAGES} currentStage={opp.stage} onSelect={handleStage} />
          </div>

          {/* ══ INFO STRIP ══ */}
          <div style={{ background: "#fff", padding: "9px 22px", borderBottom: "1px solid #e6e9f2", display: "flex", alignItems: "center", gap: "22px", flexShrink: 0, flexWrap: "wrap", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
            {/* Amount */}
            <div>
              <div style={{ fontSize: "8.5px", color: "#a3aed0", fontWeight: "800", textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: "2px" }}>Amount</div>
              {editing
                ? <input type="number" value={ef.amount || ""} onChange={e => set("amount", e.target.value)} style={{ width: "110px", padding: "4px 8px", border: "1.5px solid #3761E9", borderRadius: "6px", fontSize: "13px", fontWeight: "700", color: "#2b3674", outline: "none" }} />
                : <div style={{ fontSize: "13px", fontWeight: "700", color: "#2b3674" }}>{fmt(opp.amount)}</div>
              }
            </div>
            {/* Dates */}
            <div>
              <div style={{ fontSize: "8.5px", color: "#a3aed0", fontWeight: "800", textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: "2px" }}>Time Frames</div>
              {editing ? (
                <div style={{ display: "flex", gap: "8px" }}>
                  <input type="date" value={ef.startDate || ""} onChange={e => set("startDate", e.target.value)} style={{ padding: "4px 7px", border: "1.5px solid #3761E9", borderRadius: "6px", fontSize: "11.5px", outline: "none" }} />
                  <input type="date" value={ef.endDate || ""} onChange={e => set("endDate", e.target.value)} style={{ padding: "4px 7px", border: "1.5px solid #3761E9", borderRadius: "6px", fontSize: "11.5px", outline: "none" }} />
                </div>
              ) : (
                <div style={{ fontSize: "12.5px", fontWeight: "700", color: "#2b3674" }}>{opp.startDate && opp.endDate ? `${opp.startDate} – ${opp.endDate}` : opp.startDate || opp.endDate || "—"}</div>
              )}
            </div>
            {/* Priority */}
            <div>
              <div style={{ fontSize: "8.5px", color: "#a3aed0", fontWeight: "800", textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: "2px" }}>Priority</div>
              {editing
                ? <select value={ef.priority || ""} onChange={e => set("priority", e.target.value)} style={{ padding: "4px 22px 4px 7px", border: "1.5px solid #3761E9", borderRadius: "6px", fontSize: "12px", outline: "none", appearance: "none" }}>{PRIORITY_LEVELS.map(p => <option key={p}>{p}</option>)}</select>
                : <div style={{ display: "flex", alignItems: "center", gap: "5px" }}><div style={{ width: "7px", height: "7px", borderRadius: "50%", background: PRIORITY_COLOR[opp.priority] || "#ccc" }} /><span style={{ fontSize: "12.5px", fontWeight: "700", color: PRIORITY_COLOR[opp.priority] || "#2b3674" }}>{opp.priority || "—"}</span></div>
              }
            </div>
            <Vdiv />
            {/* Owner */}
            <div>
              <div style={{ fontSize: "8.5px", color: "#a3aed0", fontWeight: "800", textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: "4px" }}>Owner</div>
              {editing
                ? <select value={ef.assignee?.id || ""} onChange={e => { const m = teamList.find(t => String(t.id) === e.target.value); set("assignee", m || null); }} style={{ padding: "4px 22px 4px 7px", border: "1.5px solid #3761E9", borderRadius: "6px", fontSize: "12px", outline: "none", appearance: "none" }}>
                    <option value="">— Unassigned —</option>
                    {teamList.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                : assignees[0]
                  ? <div style={{ display: "flex", alignItems: "center", gap: "6px" }}><Avatar user={assignees[0]} size={20} showDot /><span style={{ fontSize: "12.5px", fontWeight: "700", color: "#2b3674" }}>{assignees[0].name}</span></div>
                  : <span style={{ fontSize: "12px", color: "#c8c8e0" }}>Unassigned</span>
              }
            </div>
            {/* Probability */}
            {opp.probability != null && (
              <div style={{ minWidth: "80px" }}>
                <div style={{ fontSize: "8.5px", color: "#a3aed0", fontWeight: "800", textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: "4px" }}>Probability</div>
                {editing
                  ? <input type="number" min="0" max="100" value={ef.probability || ""} onChange={e => set("probability", Number(e.target.value))} style={{ width: "70px", padding: "4px 7px", border: "1.5px solid #3761E9", borderRadius: "6px", fontSize: "12px", outline: "none" }} />
                  : <><ProbBar value={opp.probability} height={4} /><span style={{ fontSize: "10.5px", fontWeight: "700", color: opp.probability >= 70 ? "#00897B" : opp.probability >= 40 ? "#FB8C00" : "#E53935" }}>{opp.probability}%</span></>
                }
              </div>
            )}
            {/* Activity stats quick-view */}
            <div style={{ marginLeft: "auto", display: "flex", gap: "8px" }}>
              {[
                { label: "Today's Activities", count: todayCount,   color: "#3761E9" },
                { label: "No Activities",      count: activities.length === 0 ? 1 : 0, color: "#FB8C00" },
                { label: "Overdue",            count: overdueCount, color: "#E53935" },
              ].map(stat => (
                <div key={stat.label} style={{ textAlign: "center", padding: "3px 8px", borderRadius: "8px", background: stat.count > 0 ? stat.color + "12" : "#f5f5fb", border: `1px solid ${stat.count > 0 ? stat.color + "40" : "#e6e9f2"}`, cursor: "pointer" }}
                  onClick={() => setTab("Activities")}>
                  <div style={{ fontSize: "14px", fontWeight: "900", color: stat.count > 0 ? stat.color : "#a3aed0" }}>{stat.count}</div>
                  <div style={{ fontSize: "8.5px", color: "#a3aed0", fontWeight: "700", whiteSpace: "nowrap" }}>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ══ CONTENT ══ */}
          <div style={{ flex: 1, display: "flex", overflow: "hidden", padding: "18px 22px", gap: "16px" }}>

            {/* ── LEFT SIDEBAR ── */}
            <div style={{ width: "280px", flexShrink: 0, display: "flex", flexDirection: "column", gap: "12px", overflowY: "auto" }}>

              {/* Customer Card */}
              <SideCard title="Customer">
                <div style={{ display: "flex", gap: "10px", marginBottom: contacts.length ? "14px" : 0, alignItems: "flex-start" }}>
                  <div style={{ width: "36px", height: "36px", borderRadius: "9px", background: "#E3EAFF", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Ic n="link" s={16} c="#3761E9" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {editing
                      ? <Input value={ef.company || ""} onChange={e => set("company", e.target.value)} placeholder="Company name" />
                      : <div style={{ fontSize: "13px", fontWeight: "800", color: "#3761E9", marginBottom: "2px" }}>{opp.company || <span style={{ color: "#c0c0d8" }}>No company</span>}</div>
                    }
                  </div>
                </div>
                {contacts.map((c, ci) => (
                  <div key={c.id || ci} style={{ display: "flex", gap: "9px", alignItems: "flex-start", marginBottom: "12px", paddingTop: "10px", borderTop: "1px solid #f5f5fb" }}>
                    <div style={{ width: "34px", height: "34px", borderRadius: "50%", background: "#F3E5F5", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", fontWeight: "800", color: "#7B1FA2" }}>
                      {c.name ? c.name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() : "?"}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: "12.5px", fontWeight: "800", color: "#3761E9", marginBottom: "3px" }}>{c.name}</div>
                      {c.phone && <div style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "2px" }}><Ic n="phone" s={10} c="#a3aed0" /><a href={`tel:${c.phone}`} style={{ fontSize: "11.5px", color: "#3761E9", textDecoration: "none" }}>{c.phone}</a><button onClick={copyPhone} style={{ border: "none", background: "none", cursor: "pointer", padding: "1px 4px", borderRadius: "4px", color: copied ? "#43A047" : "#c0c0d8", fontSize: "9.5px" }}>{copied ? "✓" : <Ic n="copy" s={9} />}</button></div>}
                      {c.email && <div style={{ display: "flex", alignItems: "center", gap: "4px" }}><Ic n="mail" s={10} c="#a3aed0" /><a href={`mailto:${c.email}`} style={{ fontSize: "11.5px", color: "#3761E9", textDecoration: "none" }}>{c.email}</a></div>}
                    </div>
                  </div>
                ))}
                <button onClick={() => setShowContact(true)} style={{ width: "100%", padding: "7px", border: "1.5px dashed #c8c8e0", borderRadius: "8px", background: "transparent", cursor: "pointer", fontSize: "11.5px", color: "#3761E9", display: "flex", alignItems: "center", justifyContent: "center", gap: "5px" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "#3761E9"; e.currentTarget.style.background = "#f0f4ff"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "#c8c8e0"; e.currentTarget.style.background = "transparent"; }}>
                  <Ic n="plus" s={10} /> {contacts.length > 0 ? "Add Contact" : "Add Contact"}
                </button>
              </SideCard>

              {/* About */}
              <SideCard title="About the Opportunity">
                {editing ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: "9px" }}>
                    <Field label="Pipeline"><Sel value={ef.pipeline || ""} onChange={e => set("pipeline", e.target.value)}>{PIPELINES.map(p => <option key={p}>{p}</option>)}</Sel></Field>
                    <Field label="Source"><Sel value={ef.source || ""} onChange={e => set("source", e.target.value)}><option value="">—</option>{SOURCES.map(s => <option key={s}>{s}</option>)}</Sel></Field>
                    <Field label="Contact Method"><Sel value={ef.contactMethod || ""} onChange={e => set("contactMethod", e.target.value)}><option value="">—</option>{CONTACT_METHODS.map(m => <option key={m}>{m}</option>)}</Sel></Field>
                  </div>
                ) : (
                  <>
                    <DetailRow label="Pipeline"    value={opp.pipeline} />
                    <DetailRow label="Source"      value={opp.source} />
                    <DetailRow label="Contact via" value={opp.contactMethod} />
                    <DetailRow label="Start Date"  value={opp.startDate} />
                    <DetailRow label="Close Date"  value={opp.endDate} />
                    <DetailRow label="Budget"      value={fmt(opp.amount)} />
                  </>
                )}
                <div style={{ marginTop: "11px", paddingTop: "11px", borderTop: "1px solid #f0f0f8" }}>
                  <div style={{ fontSize: "9.5px", fontWeight: "700", color: "#a3aed0", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: "8px" }}>Tags</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
                    {(editing ? ef.tags : opp.tags)?.map((tag, i) => <TagChip key={i} tag={tag} onRemove={editing ? () => toggleTag(tag) : undefined} />)}
                    {editing && ALL_TAGS.filter(t => !ef.tags?.some(et => et.label === t.label)).map(tag => (
                      <button key={tag.label} onClick={() => toggleTag(tag)} style={{ padding: "2px 8px", borderRadius: "12px", border: `1.5px dashed ${tag.color}60`, background: "transparent", color: tag.color, fontSize: "10.5px", cursor: "pointer" }}>+ {tag.label}</button>
                    ))}
                    {!editing && <button onClick={startEdit} style={{ fontSize: "11px", color: "#3761E9", fontWeight: "700", background: "none", border: "none", cursor: "pointer", padding: "2px 4px" }}>+ Add Tag</button>}
                  </div>
                </div>
              </SideCard>

              {/* Personnel — multi-assignee */}
              <SideCard title="Personnel">
                {teamList.length === 0 ? (
                  <div style={{ padding: "10px", textAlign: "center", fontSize: "11.5px", color: "#a3aed0" }}>
                    No team members yet
                    <button onClick={() => setShowInvite(v => !v)} style={{ marginTop: "6px", display: "flex", alignItems: "center", gap: "4px", fontSize: "11.5px", color: "#3761E9", background: "none", border: "1.5px dashed #b0b0c8", borderRadius: "7px", padding: "5px 10px", cursor: "pointer", width: "100%", justifyContent: "center" }}>
                      <Ic n="mail" s={10} /> + Invite via Email
                    </button>
                  </div>
                ) : (
                  <div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "10px" }}>
                      {teamList.map(m => {
                        const isAssigned = assignees.some(a => a.id === m.id);
                        const isOwner    = assignees[0]?.id === m.id;
                        return (
                          <button key={m.id}
                            onClick={() => {
                              // Only update local state — save via "Set Owner" button
                              setAssignees(prev =>
                                isAssigned ? prev.filter(a => a.id !== m.id) : [...prev, m]
                              );
                            }}
                            style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 10px", border: `1.5px solid ${isAssigned ? (m.color || "#3761E9") : "#e0e0e8"}`, borderRadius: "9px", background: isAssigned ? (m.color || "#3761E9") + "18" : "#fff", cursor: "pointer", transition: "all .15s", width: "100%", textAlign: "left" }}
                            onMouseEnter={e => { if (!isAssigned) { e.currentTarget.style.background = "#f5f7ff"; e.currentTarget.style.borderColor = "#c8d4ff"; } }}
                            onMouseLeave={e => { if (!isAssigned) { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#e0e0e8"; } }}>
                            <Avatar user={m} size={32} showDot />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: "12.5px", fontWeight: "700", color: "#2b3674", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.name}</div>
                              <div style={{ fontSize: "10.5px", color: "#a3aed0" }}>{m.position || ""}</div>
                            </div>
                            {isOwner && <span style={{ fontSize: "9.5px", background: m.color || "#3761E9", color: "#fff", borderRadius: "5px", padding: "2px 6px", fontWeight: "800", flexShrink: 0 }}>Owner</span>}
                            {isAssigned && !isOwner && <span style={{ fontSize: "9.5px", color: m.color || "#3761E9", fontWeight: "800", flexShrink: 0 }}>✓</span>}
                          </button>
                        );
                      })}
                    </div>
                    {assignees.length > 0 && (
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                        <div style={{ fontSize: "10px", color: "#a3aed0" }}>{assignees.length} selected · First = Owner</div>
                        <button
                          onClick={() => onUpdate({ ...opp, assignee: assignees[0] || null, assignees: assignees })}
                          style={{ padding: "4px 10px", border: "none", borderRadius: "6px", background: "#3761E9", color: "#fff", fontSize: "10.5px", fontWeight: "700", cursor: "pointer" }}>
                          Save ✓
                        </button>
                      </div>
                    )}
                    <button onClick={() => setShowInvite(v => !v)} style={{ display: "flex", alignItems: "center", gap: "4px", padding: "6px 10px", border: "1.5px dashed #b0b0c8", borderRadius: "7px", background: "transparent", cursor: "pointer", fontSize: "11px", color: "#8888a8", width: "100%", justifyContent: "center" }}>
                      <Ic n="mail" s={10} /> + Invite via Email
                    </button>
                    {showInvite && (
                      <div style={{ display: "flex", gap: "6px", marginTop: "6px" }}>
                        <input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="email@company.com" type="email" style={{ flex: 1, padding: "6px 9px", border: "1.5px solid #e0e0e8", borderRadius: "7px", fontSize: "12px", outline: "none" }} />
                        <button onClick={() => { setShowInvite(false); setInviteEmail(""); }} style={{ padding: "6px 10px", border: "none", borderRadius: "7px", background: "#3761E9", color: "#fff", cursor: "pointer", fontSize: "11px", fontWeight: "600" }}>Send</button>
                      </div>
                    )}
                  </div>
                )}
              </SideCard>

              {/* Description */}
              <SideCard title="Description">
                {editing
                  ? <Textarea value={ef.notes || ""} onChange={e => set("notes", e.target.value)} placeholder="Add description or notes..." rows={4} />
                  : opp.notes
                  ? <div style={{ fontSize: "12.5px", color: "#3a3a5c", lineHeight: "1.65" }}>{opp.notes}</div>
                  : <div style={{ fontSize: "12px", color: "#c0c0d8" }}>No description</div>
                }
              </SideCard>
            </div>

            {/* ── RIGHT CONTENT ── */}
            <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "14px", overflowY: "auto" }}>

              {(tab === "Main" || tab === "Activities") && (
                <>
                  <ActivityInputBox onAdd={addActivity} currentUser={assignees[0] || opp.assignee} oppId={opp.id} />
                  <ActivityFeed activities={activities} loading={actsLoading} />
                </>
              )}

              {tab === "Documents" && (
                <div style={{ background: "#fff", borderRadius: "13px", border: "1px solid #e6e9f2", padding: "20px" }}>
                  <div style={{ fontSize: "10px", fontWeight: "800", color: "#a3aed0", textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: "14px" }}>File Attachments</div>
                  <div onClick={() => fileRef.current.click()}
                    onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = "#3761E9"; e.currentTarget.style.background = "#f0f4ff"; }}
                    onDragLeave={e => { e.currentTarget.style.borderColor = "#c8c8e0"; e.currentTarget.style.background = "#fafafa"; }}
                    onDrop={e => {
                      e.preventDefault(); e.currentTarget.style.borderColor = "#c8c8e0"; e.currentTarget.style.background = "#fafafa";
                      const dropped = Array.from(e.dataTransfer.files).map(f => ({ name: f.name, size: f.size, type: f.type, url: URL.createObjectURL(f), publicUrl: URL.createObjectURL(f), uploading: false, _file: f }));
                      setEf(p => ({ ...p, attachments: [...(p.attachments || []), ...dropped] }));
                    }}
                    style={{ border: "2px dashed #c8c8e0", borderRadius: "12px", padding: "36px 20px", textAlign: "center", cursor: "pointer", background: "#fafafa", transition: "all .15s", marginBottom: "14px" }}>
                    <div style={{ fontSize: "30px", marginBottom: "8px" }}>📎</div>
                    <div style={{ fontSize: "13px", fontWeight: "600", color: "#3761E9", marginBottom: "4px" }}>Upload or drag & drop files</div>
                    <div style={{ fontSize: "11px", color: "#a0a0b8" }}>PDF, Word, Excel, Images (max 10MB)</div>
                  </div>
                  <input ref={fileRef} type="file" multiple style={{ display: "none" }} onChange={handleDocFiles} accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp" />
                  {(ef.attachments || []).length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                      {(ef.attachments || []).map((file, i) => <AttachmentItem key={i} file={file} onRemove={() => removeDocFile(i)} />)}
                    </div>
                  )}
                </div>
              )}

              {tab === "Email" && (
                <div style={{ background: "#fff", borderRadius: "13px", border: "1px solid #e6e9f2", padding: "40px", textAlign: "center" }}>
                  <div style={{ fontSize: "36px", marginBottom: "12px" }}>✉️</div>
                  <div style={{ fontSize: "14px", fontWeight: "700", color: "#2b3674", marginBottom: "6px" }}>Email Integration</div>
                  <div style={{ fontSize: "12.5px", color: "#a3aed0", marginBottom: "16px" }}>Connect your email to track conversations with this contact</div>
                  <button onClick={() => setTab("Integrations")} style={{ padding: "8px 20px", border: "none", borderRadius: "8px", background: "#3761E9", color: "#fff", cursor: "pointer", fontSize: "12.5px", fontWeight: "600" }}>Connect Email →</button>
                </div>
              )}

              {tab === "Calls" && (
                <div style={{ background: "#fff", borderRadius: "13px", border: "1px solid #e6e9f2", padding: "40px", textAlign: "center" }}>
                  <div style={{ fontSize: "36px", marginBottom: "12px" }}>📞</div>
                  <div style={{ fontSize: "14px", fontWeight: "700", color: "#2b3674", marginBottom: "6px" }}>Call Log</div>
                  <div style={{ fontSize: "12.5px", color: "#a3aed0", marginBottom: "16px" }}>Log a call or connect your phone system to track calls</div>
                  {opp.phone && (
                    <button onClick={() => window.open(`tel:${opp.phone}`)} style={{ padding: "8px 20px", border: "none", borderRadius: "8px", background: "#2E7D32", color: "#fff", cursor: "pointer", fontSize: "12.5px", fontWeight: "600" }}>📞 Call {opp.phone}</button>
                  )}
                </div>
              )}

              {tab === "Integrations" && <IntegrationsTab />}
              {tab === "Automation" && <AutomationTab opp={opp} />}

            </div>
          </div>

          {/* ══ FOOTER ══ */}
          <div style={{ padding: "9px 22px", borderTop: "1px solid #e6e9f2", background: "#fff", display: "flex", alignItems: "center", gap: "9px", flexShrink: 0 }}>
            {confirmDel ? (
              <>
                <span style={{ fontSize: "12.5px", color: "#E53935", fontWeight: "600" }}>Permanently delete this opportunity?</span>
                <div style={{ flex: 1 }} />
                <button onClick={() => setConfirmDel(false)} style={{ padding: "6px 14px", border: "1.5px solid #e0e0e8", borderRadius: "7px", background: "#fff", cursor: "pointer", fontSize: "12px", fontWeight: "600", color: "#6e6e80" }}>Cancel</button>
                <button onClick={() => onDelete(opp.id)} style={{ padding: "6px 16px", border: "none", borderRadius: "7px", background: "#E53935", color: "#fff", cursor: "pointer", fontSize: "12px", fontWeight: "700" }}>Confirm Delete</button>
              </>
            ) : (
              <>
                <button onClick={() => setConfirmDel(true)} style={{ display: "flex", alignItems: "center", gap: "4px", padding: "6px 12px", border: "1.5px solid #ffcdd2", borderRadius: "7px", background: "#fff", cursor: "pointer", fontSize: "12px", color: "#E53935", fontWeight: "600" }}>
                  <Ic n="trash" s={10} c="#E53935" /> Delete
                </button>
                <button onClick={() => { onUpdate({ ...opp, archived: true }); onClose(); }} style={{ display: "flex", alignItems: "center", gap: "4px", padding: "6px 12px", border: "1.5px solid #e0e0e8", borderRadius: "7px", background: "#fff", cursor: "pointer", fontSize: "12px", color: "#6e6e80", fontWeight: "600" }}>
                  <Ic n="archive" s={10} c="#8888a8" /> Archive
                </button>
                <div style={{ flex: 1 }} />
                <button onClick={onClose} style={{ padding: "6px 14px", border: "1.5px solid #e0e0e8", borderRadius: "7px", background: "#fff", cursor: "pointer", fontSize: "12px", color: "#6e6e80", fontWeight: "600" }}>Close</button>
                {!editing && (
                  <button onClick={startEdit} style={{ display: "flex", alignItems: "center", gap: "5px", padding: "6px 18px", border: "none", borderRadius: "7px", background: "#3761E9", color: "#fff", cursor: "pointer", fontSize: "12px", fontWeight: "700" }}>
                    <Ic n="edit" s={10} c="#fff" /> Edit
                  </button>
                )}
              </>
            )}
          </div>

        </div>
      </div>

      {showContact && (
        <CreateContactDrawer
          onClose={() => setShowContact(false)}
          onSave={c => setContacts(prev => [...prev, c])}
        />
      )}
    </>
  );
}

// ── Wrapper ──
export default function OpportunityDetailsDrawer(props) {
  if (!props.opp) return null;
  return <OpportunityDetailsDrawerInner {...props} />;
}
